/**
 * Replies in flight, held outside React.
 *
 * A reply outlives the page that asked for it. Leave a chat while it streams
 * and the stream keeps being read here, so the chat shows it when you come
 * back; the sidebar and the usage bar keep updating meanwhile. A stream owned
 * by a component effect would instead be cut off by every unmount — including
 * the pretend one React StrictMode does on first mount in development.
 *
 * The reply itself runs in the chat's room on the Worker, and goes on when
 * the page is closed. `attachTurn` joins one this page did not start, and a
 * stream cut off mid-reply is joined again, once. Stop is its own request.
 *
 * One turn per chat. Starting another replaces it, so the page folds a
 * settled turn into its own message list first (`withTurn(…, true)`).
 *
 * A message sent while a reply streams is queued here, one per chat. When the
 * reply stops to wait and is taken on, a queued message of plain text goes
 * with the `continue` as `steer`, and the model reads it before its next
 * step; otherwise the page sends it once the reply is done.
 *
 * In a PC's Dispatch thread a plain message sent while the last reply waits on
 * the PC is held by the Worker (202), not streamed: it is kept here, per chat,
 * for the thread to show until the PC takes the reply on. Only the page that is
 * that PC runs the thread's calls (`ownsThread`); any other page leaves them
 * waiting, and a `cancel` from the hub lets go of one (`abandonTurn`).
 *
 * Whether Research is on is kept here too, per chat, and goes with every
 * request. A reply waiting on a Research job is taken on by asking where the
 * job stands every few seconds, and going on once it is over.
 */

import { useSyncExternalStore } from 'react';
import { MAX_STEER_CHARS, SERVER_WAIT_TOOLS } from '@wire';
import {
  ApiError,
  researchStatus,
  chatRetryPath,
  chatTurnPath,
  followTurn,
  stopReply,
  streamTurn,
  chatContinuePath,
  type AnswerItem,
  type Attachment,
  type ChatSummary,
  type ClientTool,
  type ContextUse,
  type ContinueRequest,
  type GeneratedImage,
  type HostContext,
  type Lookup,
  type Me,
  type MemoryChange,
  type Message,
  type PendingCall,
  type ReplyRound,
  type ToolRun,
  type TurnEvent
} from '../api';
import {
  desktopCalls,
  desktopTools,
  hostContext,
  inDesktop,
  ownDeviceId,
  ownsThread,
  runOnDesktop,
  tellDesktopChatStopped,
  tellDesktopReplyEnded,
  type DesktopResult
} from '../desktop';
import { canRunCode, pageCalls, runPageCalls, warmPython } from '../runner';

export interface TurnFailure {
  code: string;
  message: string;
  /** `limit_reached` only: when the window frees up. */
  resetsAt: number | null;
  window: string | null;
  /** Refused before anything streamed, so nothing was stored — not even the
   *  person's message. */
  refused: boolean;
}

export interface TurnInput {
  text: string;
  attachments: Attachment[];
  /** The id of the reply whose `ask` this text answers. */
  answers?: string;
  /** The question dock's answers, one per question, with `answers`. */
  picks?: string[];
  /** The same, beside their questions, for the stand-in's answer block. */
  answered?: AnswerItem[];
  /** The person's message this one is a new version of. Everything from it
   *  on gives way to the new version while the turn is shown. */
  edits?: string;
  /** Said in voice mode: the reply is written to be heard. */
  voice?: boolean;
  /** Sent from an app's panel: the app, whose `get_context` goes with it. */
  hostApp?: string;
  /** The part of an earlier reply this message answers. */
  quote?: string;
  /** The artifact this message asks to change, from its "Change this…". */
  artifact?: { id: string; title: string };
}

/** An image as the stream reports it: no file until it is made. */
export interface LiveImage {
  prompt: string;
  width: number;
  height: number;
  fileId: string | null;
  failed?: boolean;
}

export interface LiveTurn {
  seq: number;
  chatId: string;
  /** `continue`: a reply that stopped to wait on a tool, taken on. */
  kind: 'turn' | 'retry' | 'continue';
  /** What was sent, for sending it again after a refusal. Null on a retry. */
  input: TurnInput | null;
  /** The person's message: a local stand-in until the `user` event brings
   *  the stored one. */
  user: Message | null;
  /** A retry's target, hidden while its replacement streams. */
  replacing: string | null;
  /** `continue`: the waiting reply as stored, shown again if refused. */
  waiting: Message | null;
  messageId: string | null;
  model: string | null;
  text: string;
  /** What the model thought before answering, as it arrived. */
  reasoning: string;
  /** Where each round before the current one ended. */
  rounds: ReplyRound[];
  /** When the current round began (ms). */
  roundSince: number;
  /** When anything of the reply last arrived (ms). */
  lastAt: number;
  /** When the current round started thinking (ms), or null if it has not. */
  thinkingSince: number | null;
  /** How long the current round thought, fixed by its first word or call —
   *  so a live reply can say so before `done` brings the stored figure. */
  thoughtSeconds: number | null;
  /** What the model looked up, by the index the stream gives each lookup. */
  lookups: Lookup[];
  /** Indices into `lookups` that have not come back yet. */
  lookupsRunning: number[];
  /** Images the reply is making or made, by the index the stream gives each. */
  images: LiveImage[];
  /** Calls on the person's machine or a connector, by the stream's index. */
  calls: ToolRun[];
  /** What the person sent into the reply as it worked, after the rounds it
   *  followed. */
  steers: Array<{ round: number; text: string }>;
  phase: 'streaming' | 'done' | 'stopped' | 'failed';
  final: Message | null;
  context: ContextUse | null;
  title: string | null;
  suggestion: string | null;
  failure: TurnFailure | null;
}

/** The app-state writers a turn reports into. Stable callbacks from
 *  `useApp()`, safe to call after the page that started the turn is gone. */
export interface TurnApp {
  upsertChat: (chat: ChatSummary) => void;
  touchChat: (chat: ChatSummary) => void;
  patchMe: (fn: (me: Me) => Me) => void;
  refreshMe: () => Promise<void>;
}

/** A message sent while a reply streamed, waiting to go. */
export interface QueuedTurn {
  text: string;
  attachments: Attachment[];
  quote?: string;
}

const turns = new Map<string, LiveTurn>();
const queued = new Map<string, QueuedTurn>();
const researching = new Map<string, boolean>();
/** Messages a PC's thread holds for the PC (202), oldest first, by chat. */
const held = new Map<string, string[]>();
const controllers = new Map<string, AbortController>();
const memoryNotes = new Map<string, MemoryChange[]>();
/** Calls already handed to the desktop app from this page, by reply and call:
 *  a model's own call ids repeat from one reply to the next. */
const takenOn = new Set<string>();
/** Replies whose turn this page sent itself (a message, an answer, a retry),
 *  by message id, in memory alone. Only these, and their continues, bind to
 *  their chat's windows in the desktop app: a reply another page or session
 *  started is taken on with windows of its own, and after a reload asks again. */
const startedHere = new Set<string>();
const takenKey = (messageId: string, callId: string) => `${messageId}:${callId}`;
/** Turns that joined a reply another page started, by seq. */
const watching = new Set<number>();
/** Stops asked of the Worker and not yet answered, by chat: a turn sent
 *  meanwhile waits for its chat's, or it would find the reply still running. */
const stops = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();
let seq = 0;

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Update a turn, unless a newer one has replaced it — a stopped stream can
 *  still deliver a last event after the next turn has started. */
function update(chatId: string, n: number, fn: (t: LiveTurn) => LiveTurn) {
  const t = turns.get(chatId);
  if (!t || t.seq !== n) return;
  turns.set(chatId, fn(t));
  emit();
}

const now = () => Math.floor(Date.now() / 1000);

const failure = (code: string, message: string, refused: boolean): TurnFailure => ({
  code,
  message,
  resetsAt: null,
  window: null,
  refused
});

export function localUserMessage(input: TurnInput): Message {
  return {
    id: `local-user-${++seq}`,
    role: 'user',
    model: null,
    status: 'complete',
    createdAt: now(),
    body: {
      kind: 'text',
      text: input.text,
      ...(input.attachments.length ? { attachments: input.attachments } : {}),
      ...(input.answered ? { answered: input.answered } : {}),
      ...(input.quote ? { quote: input.quote } : {}),
      ...(input.artifact ? { artifact: input.artifact } : {})
    }
  };
}

export const isLocal = (id: string): boolean => id.startsWith('local-');

/** How long the current round has thought, once something fixes it. */
const thought = (t: LiveTurn, at: number): number | null =>
  t.thoughtSeconds ?? (t.thinkingSince === null ? null : (at - t.thinkingSince) / 1000);

/** Where the current round ends, as it stands. */
function roundEnd(t: LiveTurn, seconds: number | null): ReplyRound {
  return {
    reasoning: t.reasoning.length,
    text: t.text.length,
    lookups: t.lookups.length,
    calls: t.calls.length,
    images: t.images.length,
    ...(seconds === null ? {} : { thoughtSeconds: seconds })
  };
}

const sameEnd = (a: ReplyRound, b: ReplyRound) =>
  a.reasoning === b.reasoning && a.text === b.text && a.lookups === b.lookups && a.calls === b.calls && a.images === b.images;

/**
 * A turn with a piece of the reply streamed in, `at` ms. Thinking starts at
 * `thinking`, or at the first piece of reasoning from a model that skips it;
 * the round's first token or call fixes how long it lasted. A finished lookup
 * takes the place of its start. `round` ends the round before, unless nothing
 * has arrived since it ended (a reply taken on after a pause).
 */
export function streamed(t: LiveTurn, e: TurnEvent, at: number): LiveTurn {
  if (t.phase !== 'streaming') return t;
  const next = step(t, e, at);
  return next === t ? t : { ...next, lastAt: at };
}

function step(t: LiveTurn, e: TurnEvent, at: number): LiveTurn {
  switch (e.type) {
    case 'round': {
      const end = roundEnd(t, thought(t, at));
      const before = t.rounds[t.rounds.length - 1];
      return {
        ...t,
        rounds: before && sameEnd(before, end) ? t.rounds : [...t.rounds, end],
        roundSince: at,
        thinkingSince: null,
        thoughtSeconds: null
      };
    }
    case 'thinking':
      return t.thinkingSince === null ? { ...t, thinkingSince: at } : t;
    case 'reasoning':
      return { ...t, reasoning: t.reasoning + e.text, thinkingSince: t.thinkingSince ?? at };
    case 'lookup': {
      const lookups = [...t.lookups];
      lookups[e.index] = e.lookup;
      const others = t.lookupsRunning.filter((i) => i !== e.index);
      return { ...t, lookups, lookupsRunning: e.running ? [...others, e.index] : others, thoughtSeconds: thought(t, at) };
    }
    case 'tool': {
      const calls = [...t.calls];
      calls[e.index] = e.run;
      return { ...t, calls, thoughtSeconds: thought(t, at) };
    }
    case 'image': {
      const images = [...t.images];
      images[e.index] = {
        prompt: e.prompt,
        width: e.width,
        height: e.height,
        fileId: e.fileId,
        ...(e.failed ? { failed: true } : {})
      };
      return { ...t, images, thoughtSeconds: thought(t, at) };
    }
    case 'token':
      return { ...t, text: t.text + e.text, thoughtSeconds: thought(t, at) };
    case 'steer':
      return { ...t, steers: [...t.steers, { round: t.rounds.length, text: e.text }] };
    default:
      return t;
  }
}

/** Two messages sent while a reply streamed, as one: the text a paragraph
 *  apart, the files of both, and the first quote. */
export function joinQueued(a: QueuedTurn, b: QueuedTurn): QueuedTurn {
  const quote = a.quote ?? b.quote;
  return {
    text: [a.text, b.text].filter((t) => t.trim()).join('\n\n'),
    attachments: [...a.attachments, ...b.attachments],
    ...(quote ? { quote } : {})
  };
}

/** Queue a message to follow the reply streaming in this chat. */
export function queueTurn(chatId: string, next: QueuedTurn): void {
  const before = queued.get(chatId);
  queued.set(chatId, before ? joinQueued(before, next) : next);
  emit();
}

/** The queued message, taken out of the queue. */
export function takeQueued(chatId: string): QueuedTurn | null {
  const q = queued.get(chatId) ?? null;
  if (q) {
    queued.delete(chatId);
    emit();
  }
  return q;
}

export function useQueued(chatId: string | undefined): QueuedTurn | null {
  return useSyncExternalStore(subscribe, () => (chatId ? queued.get(chatId) ?? null : null));
}

const NO_HELD: readonly string[] = [];

/** A message the Worker is holding for a PC's thread until the PC picks up. */
export function holdMessage(chatId: string, text: string): void {
  held.set(chatId, [...(held.get(chatId) ?? []), text]);
  emit();
}

/** The thread no longer shows what it held: the PC took the reply on. */
export function clearHeld(chatId: string): void {
  if (held.delete(chatId)) emit();
}

export const heldFor = (chatId: string): readonly string[] => held.get(chatId) ?? NO_HELD;

export function useHeld(chatId: string | undefined): readonly string[] {
  return useSyncExternalStore(subscribe, () => (chatId ? heldFor(chatId) : NO_HELD));
}

/** Turn Research on or off for a chat. */
export function setResearch(chatId: string, on: boolean): void {
  if (on) researching.set(chatId, true);
  else researching.delete(chatId);
  emit();
}

export function useResearch(chatId: string | undefined): boolean {
  return useSyncExternalStore(subscribe, () => (chatId ? researching.get(chatId) ?? false : false));
}

/** What a waiting reply waits on that the Worker answers itself: its
 *  Research jobs, and agents at work. */
export const serverCalls = (pending: readonly PendingCall[]): PendingCall[] =>
  pending.filter((c) => c.where === 'assist' && SERVER_WAIT_TOOLS.includes(c.name) && c.needs === 'result');

/** A reply waiting on a video and on nothing but the Worker: the chat's
 *  room makes it and carries the reply on, and no page asks after it. One
 *  that also waits on the page or the machine is the page's to take on, and
 *  the clip is made as it goes on. */
export const roomCarries = (pending: readonly PendingCall[]): boolean =>
  pending.some((c) => c.where === 'assist' && c.name === 'generate_video' && c.needs === 'result') &&
  serverCalls(pending).length === pending.length;

/** How often a Research job is asked after, while a reply waits on it. */
const RESEARCH_POLL_MS = 5_000;

/** Until the Research job the reply waits on is over, or `signal` stops it.
 *  A look that fails is tried again; one the Worker refuses ends the wait,
 *  and the continue after it says why. */
async function waitResearch(chatId: string, messageId: string, signal: AbortSignal): Promise<void> {
  while (!signal.aborted) {
    try {
      if ((await researchStatus(chatId, messageId)).state !== 'running') return;
    } catch (err) {
      if (err instanceof ApiError && err.status !== 0) return;
    }
    await new Promise<void>((done) => {
      const t = setTimeout(done, RESEARCH_POLL_MS);
      signal.addEventListener('abort', () => (clearTimeout(t), done()), { once: true });
    });
  }
}

/** Whether a queued message can go into a reply as it works: plain text that
 *  fits, with no files, no quote and no command. */
export const steerable = (q: QueuedTurn): boolean =>
  !q.attachments.length && !q.quote && !q.text.trimStart().startsWith('/') && !!q.text.trim() && q.text.length <= MAX_STEER_CHARS;

/** A turn's reply as a stored one had it, to go on from. */
const goesOnFrom = (had: Extract<Message['body'], { kind: 'text' }> | null) => ({
  text: had?.text ?? '',
  reasoning: had?.reasoning ?? '',
  rounds: had?.rounds ?? [],
  // A reply that kept its rounds has its own figure in each.
  thoughtSeconds: had?.rounds ? null : (had?.thoughtSeconds ?? null),
  lookups: had?.lookups ?? [],
  lookupsRunning: [],
  images: had?.images ?? [],
  calls: had?.calls ?? [],
  steers: had?.steers ?? []
});

/**
 * Join the reply running in a chat: what it has so far, then the rest as it
 * comes; nothing when none is running. `owner`: this page started it and lost
 * its stream, so its pauses are this page's to take on. A page that only
 * joined leaves them to the one that started it.
 */
export function attachTurn(opts: { chat: ChatSummary; app: TurnApp; owner?: boolean }): void {
  if (turns.get(opts.chat.id)?.phase === 'streaming') return;
  startTurn({ chat: opts.chat, kind: 'turn', app: opts.app, join: { owner: !!opts.owner } });
}

export function startTurn(opts: {
  chat: ChatSummary;
  kind: 'turn' | 'retry' | 'continue';
  input?: TurnInput;
  replacing?: string;
  /** `continue`: the waiting reply, and what to take it on with. */
  waiting?: Message;
  resume?: ContinueRequest;
  /** `continue`: the person's message from the turn this one takes over,
   *  drawn until the page has folded that turn in. */
  carry?: Message | null;
  app: TurnApp;
  /** Join the reply running in the chat rather than start one (`attachTurn`). */
  join?: { owner: boolean };
}): void {
  const { chat, kind, app } = opts;
  const join = opts.join ?? null;
  const owner = !join || join.owner;
  const before = turns.get(chat.id);
  // A reply still streaming is stopped: this page stops reading it, and the
  // Worker stops it for this one. One that is done keeps its stream, so its
  // title and memory still arrive.
  if (before?.phase === 'streaming') controllers.get(chat.id)?.abort();
  const replaceRunning = !join && before?.phase === 'streaming';

  const n = ++seq;
  if (!owner) watching.add(n);
  // Another PC's thread: its calls are that PC's to run, and 403 here.
  const mine = ownsThread(chat.deviceId, ownDeviceId());
  const input = opts.input ?? null;
  // Code in a message is likely to be run: Pyodide starts loading now, while
  // the model reads it, rather than when it asks.
  if (kind === 'turn' && input?.text.includes('```') && canRunCode()) warmPython();
  // Taken on after a pause: the reply goes on from what it had, in its row.
  const had = opts.waiting?.body.kind === 'text' ? opts.waiting.body : null;
  const started = Date.now();
  turns.set(chat.id, {
    seq: n,
    chatId: chat.id,
    kind,
    input,
    user: kind === 'turn' && input ? localUserMessage(input) : (opts.carry ?? null),
    replacing: opts.replacing ?? null,
    waiting: opts.waiting ?? null,
    messageId: opts.waiting?.id ?? null,
    model: opts.waiting?.model ?? null,
    ...goesOnFrom(had),
    roundSince: started,
    lastAt: started,
    thinkingSince: null,
    phase: 'streaming',
    final: null,
    context: null,
    title: null,
    suggestion: null,
    failure: null
  });
  emit();

  const controller = new AbortController();
  controllers.set(chat.id, controller);
  let base = chat;
  let replyId: string | null = null;
  let waitingOn: Message | null = null;
  /** A queued message sent as `steer`, until the Worker says it has it. */
  let steer: QueuedTurn | null = null;
  /** Taken on again once it settled: the reply goes on, and keeps its windows. */
  let goesOn = false;

  const onEvent = (e: TurnEvent) => {
    switch (e.type) {
      // Joined: the reply as it began, then its events so far.
      case 'attach': {
        if (e.messageId) replyId = e.messageId;
        const from = e.base?.kind === 'text' ? e.base : null;
        update(chat.id, n, (t) => ({ ...t, kind: e.kind, messageId: e.messageId, ...goesOnFrom(from), thinkingSince: null }));
        break;
      }
      case 'user':
        update(chat.id, n, (t) => ({ ...t, user: e.message }));
        break;
      case 'start':
        replyId = e.messageId;
        if (kind !== 'continue' && !join) startedHere.add(e.messageId);
        update(chat.id, n, (t) => ({ ...t, messageId: e.messageId, model: e.model }));
        break;
      case 'round':
      case 'thinking':
      case 'reasoning':
      case 'lookup':
      case 'image':
      case 'tool':
      case 'token':
        update(chat.id, n, (t) => streamed(t, e, Date.now()));
        break;
      case 'steer':
        steer = null;
        update(chat.id, n, (t) => streamed(t, e, Date.now()));
        break;
      // Waiting on a tool: settled as it stands, `pending` and all. The page
      // draws what it waits on; `continue` takes it on.
      case 'tool_request':
      case 'done':
        replyId = e.message.id;
        waitingOn = e.type === 'tool_request' ? e.message : null;
        // Joined, its pauses are for the page that started it.
        if (!owner && e.message.body.kind === 'text') {
          for (const c of e.message.body.pending ?? []) takenOn.add(takenKey(e.message.id, c.id));
        }
        // As the Worker has it: any call left pending, or a step limit, waits
        // for you. A Research job this page is waiting on does not.
        base = {
          ...base,
          updatedAt: now(),
          needsYou:
            e.type === 'tool_request' &&
            e.message.body.kind === 'text' &&
            ((e.message.body.pending ?? []).length > serverCalls(e.message.body.pending ?? []).length || !!e.message.body.stepLimit)
        };
        app.patchMe((me) => ({ ...me, usage: e.usage }));
        app.touchChat(base);
        update(chat.id, n, (t) => ({
          ...t,
          phase: 'done',
          final: e.message,
          messageId: e.message.id,
          text: e.message.body.kind === 'text' ? e.message.body.text : t.text,
          context: e.context
        }));
        break;
      case 'title':
        base = { ...base, title: e.title };
        app.upsertChat(base);
        update(chat.id, n, (t) => ({ ...t, title: e.title }));
        break;
      case 'suggestion':
        update(chat.id, n, (t) => ({ ...t, suggestion: e.text }));
        break;
      case 'memory':
        if (replyId && e.changes.length) memoryNotes.set(replyId, e.changes);
        update(chat.id, n, (t) => ({ ...t }));
        break;
      case 'usage':
        app.patchMe((me) => ({ ...me, usage: e.usage }));
        break;
      case 'finished':
        break;
      case 'error':
        if (e.code === 'ai_disabled') void app.refreshMe();
        update(chat.id, n, (t) => ({
          ...t,
          // It typed an image's record rather than making one: not kept.
          ...(e.code === 'no_images' ? { text: '' } : {}),
          phase: 'failed',
          failure: { ...failure(e.code, e.message, false), resetsAt: e.resetsAt ?? null }
        }));
        break;
    }
  };

  const path =
    kind === 'turn'
      ? chatTurnPath(chat.id)
      : kind === 'continue'
        ? chatContinuePath(chat.id, opts.waiting?.id ?? '')
        : chatRetryPath(chat.id);
  const ids = input?.attachments.map((a) => a.fileId) ?? [];
  const body = (clientTools: ClientTool[] | undefined, results: DesktopResult[], host: HostContext | null = null) => {
    const tools = {
      ...(clientTools ? { clientTools } : {}),
      ...(canRunCode() ? { runsCode: true } : {}),
      ...(researching.get(chat.id) ? { research: true } : {})
    };
    if (kind === 'turn') {
      return {
        text: input?.text ?? '',
        ...(ids.length ? { attachments: ids } : {}),
        ...(input?.answers ? { answers: input.answers } : {}),
        ...(input?.answers && input.picks ? { picks: input.picks } : {}),
        ...(input?.edits ? { edits: input.edits } : {}),
        ...(input?.quote ? { quote: input.quote } : {}),
        ...(input?.artifact ? { artifact: input.artifact.id } : {}),
        ...(input?.voice ? { voice: true } : {}),
        ...(host ? { host } : {}),
        ...(replaceRunning ? { replaceRunning: true } : {}),
        ...tools
      };
    }
    if (kind === 'continue') {
      const sent = [...(opts.resume?.results ?? []), ...results];
      // What the person queued as it worked goes in with the results.
      const q = queued.get(chat.id);
      if (!opts.resume?.stop && q && steerable(q)) steer = takeQueued(chat.id);
      return {
        ...(opts.resume ?? {}),
        ...(sent.length ? { results: sent } : {}),
        ...(steer ? { steer: steer.text.trim() } : {}),
        ...tools
      };
    }
    return { messageId: opts.replacing ?? '', ...(replaceRunning ? { replaceRunning: true } : {}), ...tools };
  };

  // Taken on: code the reply asked to run runs here first, and inside the
  // desktop app what it left for the machine runs there, each row "Running"
  // meanwhile.
  const answered = new Set((opts.resume?.results ?? []).map((r) => r.callId));
  const inPage = had?.pending ? pageCalls(had.pending).filter((c) => !answered.has(c.id)) : [];
  const local = inDesktop() && mine && had?.pending ? desktopCalls(had.pending).filter((c) => !answered.has(c.id)) : [];
  // A stop ends the reply where it stands; nothing else is waited on.
  const research = had?.pending && !opts.resume?.stop ? serverCalls(had.pending) : [];
  // A Research job's row keeps saying it is researching.
  if (inPage.length || local.length) {
    const running = new Set([...inPage, ...local].map((c) => c.id));
    update(chat.id, n, (t) => ({
      ...t,
      calls: t.calls.map((c) => (running.has(c.id) ? { ...c, state: 'running' } : c))
    }));
  }

  // On the web nothing else waits: with no code to run, the stream starts at
  // once, as it always has.
  const stream = (async (): Promise<boolean> => {
    if (join) return followTurn(chat.id, onEvent, controller.signal);
    const stopping = stops.get(chat.id);
    if (stopping) await stopping;
    const ran = inPage.length ? await runPageCalls(inPage, controller.signal) : [];
    if (research.length) await waitResearch(chat.id, opts.waiting!.id, controller.signal);
    if (controller.signal.aborted) {
      // Stopped while its job ran: the reply ends there. The job is billed
      // by the Worker's sweep once it is done.
      const t = turns.get(chat.id);
      if (research.length && t?.seq === n && t.phase === 'stopped') {
        void streamTurn(chatContinuePath(chat.id, opts.waiting!.id), { stop: true }, () => {}, new AbortController().signal).catch(() => {});
      }
      return true;
    }
    // The Worker held a plain message for a PC that has not picked up: no
    // stream follows, and the thread shows the message as held.
    const sentOn = async (sent: ReturnType<typeof body>): Promise<void> => {
      if ((await streamTurn(path, sent, onEvent, controller.signal)) !== 'queued') return;
      if (kind === 'turn' && input) holdMessage(chat.id, input.text);
      if (turns.get(chat.id)?.seq === n) {
        turns.delete(chat.id);
        emit();
      }
    };
    if (!inDesktop()) {
      await sentOn(body(undefined, ran));
      return true;
    }
    const results = [
      ...ran,
      ...(local.length
        ? await runOnDesktop(local, opts.waiting!.id, chat.id, controller.signal, startedHere.has(opts.waiting!.id))
        : [])
    ];
    const tools = await desktopTools();
    // From a panel, what its app has open goes with the turn. A retry or
    // a continue reads what the person's message kept.
    const host = kind === 'turn' && input?.hostApp ? await hostContext(input.hostApp, tools) : null;
    const sent = body(tools, results, host);
    if (!controller.signal.aborted) await sentOn(sent);
    return true;
  })();

  // Cut off mid-reply, the connection went, not the model: the reply goes on
  // in its room, and is joined there again, once. Not reaching it is being
  // cut off still, never a refusal of what was sent.
  const cutOff = () => {
    const t = turns.get(chat.id);
    return !controller.signal.aborted && t?.seq === n && t.phase === 'streaming';
  };
  const followAgain = () => followTurn(chat.id, onEvent, controller.signal).catch(() => true);
  stream
    .then(
      (found) => (found && cutOff() ? followAgain() : found),
      (err: unknown) => {
        if (err instanceof ApiError || !cutOff()) throw err;
        return followAgain();
      }
    )
    .then((found) => {
      if (controller.signal.aborted) return;
      // Nothing was running: the reply ended out of sight, and the page
      // fetches the chat as it is now.
      if (!found) {
        update(chat.id, n, (t) =>
          t.phase === 'streaming' ? { ...t, phase: 'failed', failure: failure('ended', 'The reply did not finish.', false) } : t
        );
        return;
      }
      // Closed without `done` or `error` twice over: the connection went.
      update(chat.id, n, (t) =>
        t.phase === 'streaming'
          ? { ...t, phase: 'failed', failure: failure('network', 'The reply did not finish.', false) }
          : t
      );
      // Stopped for the page or the machine, while nothing has replaced this turn.
      const t = turns.get(chat.id);
      if (owner && waitingOn && t?.seq === n) goesOn = takeOn({ chat: base, message: waitingOn, app, carry: t.user });
    })
    .catch((err: unknown) => {
      const refused = err instanceof ApiError;
      // Refused, the Worker never had it: it waits in the queue again.
      if (refused && steer) queueTurn(chat.id, joinQueued(steer, takeQueued(chat.id) ?? { text: '', attachments: [] }));
      // Another page's reply is running here: a plain message waits for it
      // in the queue, and this page joins that reply.
      if (refused && err.code === 'busy' && !join) {
        if (kind === 'turn' && input && !input.edits && !input.answers && !input.voice) {
          queueTurn(chat.id, { text: input.text, attachments: input.attachments, ...(input.quote ? { quote: input.quote } : {}) });
        }
        if (turns.get(chat.id)?.seq === n) turns.delete(chat.id);
        attachTurn({ chat: base, app });
        return;
      }
      // Carried on first, by another page or by Assist once its Research was
      // over: nothing was refused, and the chat is fetched as it is now.
      if (refused && kind === 'continue' && err.status === 409 && err.details.field === 'messageId') {
        update(chat.id, n, (t) => ({ ...t, phase: 'failed', failure: failure('ended', 'The reply did not finish.', false) }));
        return;
      }
      const code = refused ? err.code : 'network';
      if (code === 'ai_disabled') void app.refreshMe();
      const details = refused ? err.details : {};
      update(chat.id, n, (t) => ({
        ...t,
        phase: 'failed',
        failure: {
          code,
          // An ApiError is a refusal before streaming; anything else broke
          // the stream part-way.
          message: !refused ? 'The reply did not finish.' : err.status !== 0 ? err.message : 'Assist did not answer.',
          resetsAt: typeof details.resetsAt === 'number' ? details.resetsAt : null,
          window: typeof details.window === 'string' ? details.window : null,
          refused
        }
      }));
    })
    .finally(() => {
      if (controllers.get(chat.id) === controller) controllers.delete(chat.id);
      // Done, stopped, failed or waiting for the person: the desktop lets go
      // of the windows this reply was given. Not when it goes on — from here,
      // or from the chat, which takes a waiting reply on too and can get there
      // first; then the takeOn above answers false for a reply that goes on.
      const ended = replyId ?? opts.waiting?.id;
      const goesOnElsewhere = !!waitingOn && takenOnAll(waitingOn);
      if (ended && owner && mine && !goesOn && !goesOnElsewhere) void tellDesktopReplyEnded(ended);
    });
}

/**
 * Takes on a reply that waits only on this page or the machine: its code runs
 * here, its calls for the machine run in the desktop app, which asks the
 * person where it must, and the reply goes on. False for a reply that also
 * waits for the person, for one that waits on the machine outside the desktop
 * app, and for calls already handed over from this page — unless `again`,
 * which is the person's own Retry after a refused take-on.
 */
export function takeOn(opts: {
  chat: ChatSummary;
  message: Message;
  app: TurnApp;
  carry?: Message | null;
  again?: boolean;
}): boolean {
  const pending = opts.message.body.kind === 'text' ? (opts.message.body.pending ?? []) : [];
  // A PC's thread asks its PC alone for the machine's calls.
  const machine = inDesktop() && ownsThread(opts.chat.deviceId, ownDeviceId());
  const here = [...pageCalls(pending), ...(machine ? desktopCalls(pending) : []), ...serverCalls(pending)];
  if (!pending.length || roomCarries(pending) || here.length !== pending.length) return false;
  const key = (c: PendingCall) => takenKey(opts.message.id, c.id);
  if (opts.again) pending.forEach((c) => takenOn.delete(key(c)));
  if (pending.some((c) => takenOn.has(key(c)))) return false;
  pending.forEach((c) => takenOn.add(key(c)));
  startTurn({ chat: opts.chat, kind: 'continue', waiting: opts.message, resume: {}, app: opts.app, carry: opts.carry ?? null });
  return true;
}

/** Every call the message waits on has been taken on from this page. */
function takenOnAll(message: Message): boolean {
  const pending = message.body.kind === 'text' ? (message.body.pending ?? []) : [];
  return pending.length > 0 && pending.every((c) => takenOn.has(takenKey(message.id, c.id)));
}

/** Stop generating. The Worker stops the reply and stores what it had; the
 *  page keeps what arrived. The turn as it now stands, for a page folding it
 *  in before the next. In the desktop app the chat's windows go too, unless
 *  `replaced`: a message sent now takes the reply's place. */
export function stopTurn(chatId: string, replaced = false): LiveTurn | null {
  const t = turns.get(chatId);
  if (!replaced) void tellDesktopChatStopped(chatId);
  controllers.get(chatId)?.abort();
  if (t && t.phase === 'streaming') {
    update(chatId, t.seq, (x) => ({ ...x, phase: 'stopped' }));
    const stopping: Promise<void> = stopReply(chatId)
      .catch(() => {})
      .finally(() => {
        if (stops.get(chatId) === stopping) stops.delete(chatId);
      });
    stops.set(chatId, stopping);
  }
  return turns.get(chatId) ?? null;
}

/** The turn this page holds in a chat, if any. */
export const peekTurn = (chatId: string): LiveTurn | null => turns.get(chatId) ?? null;

/** A reply is streaming through this page in the chat. */
export const isStreaming = (chatId: string): boolean => turns.get(chatId)?.phase === 'streaming';

/**
 * Let go of a reply the hub said was stopped elsewhere (`cancel`): this page
 * reads it no more, runs no further call for it and sends no continue. A call
 * already running finishes and its result goes nowhere. `messageId` null is
 * the whole thread gone; otherwise only a turn on that reply is let go of.
 * True when there was one to let go of.
 */
export function abandonTurn(chatId: string, messageId: string | null): boolean {
  const t = turns.get(chatId);
  if (!t || t.phase !== 'streaming') return false;
  if (messageId !== null && t.messageId !== messageId) return false;
  controllers.get(chatId)?.abort();
  turns.delete(chatId);
  emit();
  return true;
}

/** Called with the chat's turn each time it changes; returns the unsubscribe. */
export function watchTurn(chatId: string, fn: (turn: LiveTurn | null) => void): () => void {
  const look = () => fn(turns.get(chatId) ?? null);
  return subscribe(look);
}

/** Back on a page whose turn was cut off part-way: join the reply again, as
 *  the turn did. */
export function rejoinTurn(opts: { chat: ChatSummary; app: TurnApp }): void {
  const t = turns.get(opts.chat.id);
  if (t?.phase !== 'failed' || t.failure?.code !== 'network' || t.failure.refused) return;
  attachTurn({ ...opts, owner: !watching.has(t.seq) });
}

/**
 * Forget a settled turn, once the page's own list has it. Showing another
 * version of a message needs this: the turn laid over the list would put
 * back the version it wrote. A turn still streaming is kept.
 */
export function forgetTurn(chatId: string): void {
  const t = turns.get(chatId);
  if (!t || t.phase === 'streaming') return;
  turns.delete(chatId);
  emit();
}

export function useLiveTurn(chatId: string): LiveTurn | null {
  return useSyncExternalStore(subscribe, () => turns.get(chatId) ?? null);
}

/** What memory upkeep changed after a reply, if anything, this session. */
export const memoryFor = (messageId: string): MemoryChange[] | null => memoryNotes.get(messageId) ?? null;

/**
 * The reply as a message once it has settled: the stored one from `done`, or
 * — stopped or failed part-way — what had arrived, under the id `start` gave
 * it, which is the id the Worker stores it under. A lookup that never came
 * back is left out.
 */
export function replyOf(t: LiveTurn): Message | null {
  if (t.final) return t.final;
  const images = madeImages(t.images);
  if (t.phase === 'streaming' || (!t.text && !images.length)) return null;
  const lookups = t.lookups.filter((_, i) => !t.lookupsRunning.includes(i));
  return {
    id: t.messageId ?? `local-reply-${t.seq}`,
    role: 'assistant',
    model: t.model,
    status: t.phase === 'stopped' ? 'partial' : 'error',
    createdAt: now(),
    body: {
      kind: 'text',
      text: t.text,
      ...(t.reasoning ? { reasoning: t.reasoning } : {}),
      ...(t.thoughtSeconds !== null ? { thoughtSeconds: t.thoughtSeconds } : {}),
      ...(lookups.length ? { lookups } : {}),
      ...(images.length ? { images } : {}),
      ...(t.calls.length ? { calls: t.calls } : {}),
      ...(t.steers.length ? { steers: t.steers } : {}),
      // As the Worker keeps them: every round, the last one too.
      ...(t.rounds.length
        ? { rounds: [...t.rounds, { ...roundEnd(t, t.thoughtSeconds), lookups: lookups.length, images: images.length }] }
        : {})
    }
  };
}

/** The images that were made, as a stored reply keeps them. */
export const madeImages = (images: readonly (LiveImage | undefined)[]): GeneratedImage[] =>
  images.flatMap((i) => (i?.fileId ? [{ fileId: i.fileId, prompt: i.prompt, width: i.width, height: i.height }] : []));

/**
 * A chat's messages with a turn laid over them. Idempotent by id, so it is
 * safe over a list that already holds some of the turn (a chat fetched again
 * mid-reply, or a turn already folded in).
 *
 * `commit` is for folding a settled turn into the page's own list before the
 * next one replaces it: a refused message was never stored, so it goes.
 */
export function withTurn(messages: Message[], t: LiveTurn | null, commit = false): Message[] {
  if (!t) return messages;
  let list = messages;

  // A refused retry changed nothing, so its target stays.
  const retryRefused = t.kind === 'retry' && !!t.failure?.refused;
  if (t.replacing && !retryRefused) list = list.filter((m) => m.id !== t.replacing);

  // An edit: the old version and what followed it give way. Refused, it was
  // never stored, so they come back.
  const edited = t.input?.edits;
  if (edited && !t.failure?.refused) {
    const at = list.findIndex((m) => m.id === edited);
    if (at >= 0) list = list.slice(0, at);
  }

  // A row the Worker made for the reply at `start` is drawn by the live row
  // while it streams, and replaced by the settled reply after. A refused
  // continue took nothing on: the reply still waits, as the list has it.
  const continueRefused = t.kind === 'continue' && !!t.failure?.refused;
  if (t.messageId && !continueRefused) list = list.filter((m) => m.id !== t.messageId);

  // A carried message was stored, refused or not.
  if (t.user && !(commit && t.failure?.refused && isLocal(t.user.id))) {
    const user = t.user;
    if (!list.some((m) => m.id === user.id)) list = [...list, user];
  }

  // Its own copy of the waiting reply only when the list has none yet: the
  // list's is newer, as after a refetch.
  const waiting = t.waiting;
  const reply = continueRefused ? (waiting && !list.some((m) => m.id === waiting.id) ? waiting : null) : replyOf(t);
  if (reply) list = [...list, reply];
  return list;
}
